import debounce from "lodash.debounce";

// How long after one filter change the next still counts as the same edit.
const FILTER_BURST_MS = 1500;

const DEFAULT_IGNORE_KEYS = ["s", "sorts"];
const PAGINATION_KEYS = ["page", "perPage"];
export const VIEW_STATE_KEYS = ["tab", "view", "direction", "fleetchart"];

// Every route query key that is never a filter, whatever list it is on.
export const FILTER_EXCLUDE_KEYS = [
  ...DEFAULT_IGNORE_KEYS,
  ...PAGINATION_KEYS,
  ...VIEW_STATE_KEYS,
];

export const useFilters = <T>({
  ignoreKeys,
  viewKeys = [],
  updateCallback,
}: {
  ignoreKeys?: string[];
  viewKeys?: string[];
  updateCallback?: (() => void) | (() => Promise<void>);
} = {}) => {
  const route = useRoute();

  // View state that lives in the URL so a reload restores it, and that no
  // endpoint has ever been asked to filter on. `getQuery` spreads the whole
  // route query into `q`, and the query schemas are `additionalProperties:
  // false` -- so a key like this reaches the API as an unknown filter and comes
  // back a 400, which reads as a server error.
  const viewStateKeys = [...VIEW_STATE_KEYS, ...viewKeys];

  const excludeKeys = [
    ...DEFAULT_IGNORE_KEYS,
    ...PAGINATION_KEYS,
    ...viewStateKeys,
  ];

  // A ransack `_in` / `_not_in` filter takes a list, but the URL hands a single
  // value back as a bare string, which a multi-select cannot bind and the API
  // drops -- the filter silently stops filtering.
  const asLists = <Q extends Record<string, unknown>>(query: Q) => {
    Object.keys(query)
      .filter((key) => key.endsWith("In"))
      .forEach((key) => {
        const value = query[key];
        // Left as it is, an empty value is dropped below like any unset filter.
        if (value == null || value === "" || Array.isArray(value)) return;

        (query as Record<string, unknown>)[key] = [value];
      });

    return query;
  };

  const filters = computed<T>(() => {
    const query = asLists({ ...(route.query || {}) });

    Object.keys(query)
      .filter((key) => excludeKeys.includes(key))
      .forEach((key) => delete query[key]);

    return query as T;
  });

  watch(
    () => filters.value,
    async () => {
      if (!updateCallback) return;

      await updateCallback();
    },
    { deep: true },
  );

  const getQuery = (formData?: T) => {
    const query = asLists({
      ...route.query,
      ...(formData || filters.value),
    });

    Object.keys(query)
      .filter(
        (key) =>
          (!query[key] && !Object.is(query[key], 0)) ||
          query[key]?.length === 0,
      )
      .forEach((key) => delete query[key]);

    Object.keys(query)
      .filter((key) => [...PAGINATION_KEYS, ...viewStateKeys].includes(key))
      .forEach((key) => delete query[key]);

    return query;
  };

  const isFilterSelected = computed(() => {
    const query = getQuery();

    Object.keys(query)
      .filter((key) => {
        return [...(ignoreKeys || []), ...DEFAULT_IGNORE_KEYS].includes(key);
      })
      .forEach((key) => delete query[key]);

    return Object.keys(query).length > 0;
  });

  const router = useRouter();

  /*
   * View state is excluded from `getQuery` because it is not a filter -- but it
   * still belongs in the URL, so every navigation this composable makes has to
   * carry it back. Without this, filtering a list dropped the tab it was on and
   * the page fell back to its default view.
   */
  const viewState = computed(() =>
    Object.fromEntries(
      Object.entries(route.query).filter(([key]) =>
        viewStateKeys.includes(key),
      ),
    ),
  );

  // Compared loosely: the URL hands a single value back as a string and a
  // form keeps it as a one-item list, and the two are the same filter.
  const comparable = (query: Record<string, unknown>) =>
    JSON.stringify(
      Object.keys(query)
        .sort()
        .map((key) => [key, [query[key]].flat().map(String)]),
    );

  // A narrower result rarely has the page the reader was on, so a changed
  // filter starts again from the first. An unchanged one -- the form writing
  // back what it read from the URL -- leaves the page alone.
  const shouldResetPage = (query: ReturnType<typeof getQuery>) =>
    comparable(query) !== comparable(getQuery());

  // Every filter change is a step the back gesture can undo - except within
  // one burst: a search typed with pauses lands here once per pause, and each
  // of those would otherwise be its own entry to step back through.
  //
  // Arriving on the page starts a burst too, and so does the back or forward
  // gesture landing on another entry. A form writing what it read back into
  // the URL in its own shape, or filling in a default, does so right then;
  // pushing that would leave an entry the back gesture lands on only to be
  // pushed forward again. Other navigations - a page turn, a record opened -
  // are a step of their own, which a filter right after must not overwrite.
  let lastFilteredAt = 0;
  let routeChangedAt = Date.now();

  // The history only reports the back and forward gestures, never a push.
  const stopListening = router?.options?.history?.listen?.(() => {
    routeChangedAt = Date.now();
  });

  if (getCurrentScope()) onScopeDispose(() => stopListening?.());

  const navigationFor = (changed: boolean): "push" | "replace" => {
    const now = Date.now();
    const sameBurst =
      now - Math.max(lastFilteredAt, routeChangedAt) < FILTER_BURST_MS;

    if (!changed) {
      return "replace";
    }

    lastFilteredAt = now;

    return sameBurst ? "replace" : "push";
  };

  const debouncedFilter = (filter: T) => {
    const query = getQuery(filter);
    const changed = shouldResetPage(query);

    router[navigationFor(changed)]({
      ...route,
      hash: undefined,
      query: {
        ...query,
        ...viewState.value,
        page: changed ? undefined : route.query.page,
        // A page size in the URL came with a shared link. Filtering keeps
        // it; a reset goes back to the reader's own, which also drops one
        // the API refused.
        perPage: route.query.perPage,
      },
    }).catch(() => {});
  };

  const resetFilter = () =>
    router
      .push({
        ...route,
        // Clearing the filters is not leaving the view they were set in.
        query: { ...viewState.value },
      })
      .catch(() => {});

  const hasResettableQuery = computed(() =>
    Object.keys(route.query).some(
      (key) => !viewStateKeys.includes(key) && key !== "perPage",
    ),
  );

  const filter = debounce(debouncedFilter, 300);

  // An edit still in the debounce would otherwise land after its form is gone.
  if (getCurrentScope()) onScopeDispose(() => filter.cancel());

  return {
    isFilterSelected,
    hasResettableQuery,
    resetFilter,
    filter,
    filters,
    getQuery,
  };
};
